import { useMemo, useState, useCallback, useContext, useEffect } from "react";
import {
  Button,
  Divider,
  Stack,
  Checkbox,
  IconButton,
  Autocomplete,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Delete,
  CopyAll,
  Edit,
  CheckBoxOutlineBlank,
  CheckBox,
  Clear as ClearIcon,
} from "@mui/icons-material";
import {
  PlusOneOutlined,
  DeleteOutline,
  UndoOutlined,
  FileDownload,
} from "@mui/icons-material";
import {
  optionalColumns,
  addSetpointToDeviceParams,
  defaultDeviceParams,
  lookupNameOrDisplayNameToId,
  matchesSourceType,
  parseHwioChannelNumber,
  readArrayFromStorage,
  parseJsonObject,
} from "../helpers/Helpers";
import {
  getIncompatibleColumnMessage,
  isColumnCompatibleWithTag,
  MODBUS_TCP_SOURCE_TYPE_NAMES,
} from "../helpers/SourceTypeColumns";
import { hasValue } from "../../common/helpers/Utils";
import { exportToExcel, exportSelectedToExcel } from "../helpers/ExcelExport";
import { updateDirtyTags } from "../helpers/TagGridOperations";
import { UniversalDescriptorSelectCell } from "./GridCells/UniversalDescriptorSelectCell";
import { CodeLegendContext } from "../../common/contexts/CodeLegendContext";
import { LookupValueContext } from "../../common/contexts/LookupValueContext";
import SelectDistro from "./SelectDistro";
import { DataGrid, TextDisplayCell } from "../../common/components/DataGrid";
import _ from "lodash";
import {
  createTextColumn,
  visibleColumnOrderStorageKey,
  visibleColumnsStorageKey,
} from "../../common/components/DataGrid/DataGridHelper";
import {
  createOptionalColumnDefs,
  optionalColumnLabels,
} from "../helpers/OptionalColumns";
import ConfirmDialog from "../../common/components/ConfirmDialog";

const icon = <CheckBoxOutlineBlank fontSize='small' />;
const checkedIcon = <CheckBox fontSize='small' />;
const setpointColumns = [
  "LoSetpoint",
  "LoLoSetpoint",
  "HiSetpoint",
  "HiHiSetpoint",
];
// HardWiredIO-only columns. Each is transient: it is parsed out of, and written back into, a real
// tag field (TagTypeId, ChannelNumber, PortNumber) by handleRowEdit.
const hwioColumns = ["HwioIOType", "HwioChannelNumber", "HwioBoardNumber"];

const removeTransientTagFields = (row) => {
  const nextRow = { ...row };
  delete nextRow.Hidden;
  delete nextRow.SafetyShutdown;
  delete nextRow.ModbusRegister;
  delete nextRow.ModbusIP;
  delete nextRow.ModbusPort;
  delete nextRow.ModbusSlave;
  delete nextRow.LoSetpoint;
  delete nextRow.LoLoSetpoint;
  delete nextRow.HiSetpoint;
  delete nextRow.HiHiSetpoint;
  hwioColumns.forEach((columnId) => delete nextRow[columnId]);
  return nextRow;
};
const TagDataGridWrapper = ({
  data,
  tags,
  setData,
  distro,
  setDistro,
  addNewEmptyTag,
  clearGrid,
  resetChanges,
  deleteRow,
  duplicateTag,
  openTagDialog,
  selectedTags,
  setSelectedTags,
  setInfoMessages,
  dirtyTags,
  setDirtyTags,
  tagStandardTags,
  selectedColumns,
  setSelectedColumns,
  onBulkRemoveSelected,
}) => {
  const theme = useTheme();
  const { codeLegends } = useContext(CodeLegendContext);
  const lookupValues = useContext(LookupValueContext);

  const [columnOrder, setColumnOrder] = useState(() =>
    readArrayFromStorage(visibleColumnOrderStorageKey, null)
  );
  const [bulkRemoveConfirmOpen, setBulkRemoveConfirmOpen] = useState(false);
  const [visibleRowIds, setVisibleRowIds] = useState([]);

  const handleVisibleRowIdsChange = useCallback((nextVisibleRowIds) => {
    setVisibleRowIds((currentVisibleRowIds) => {
      if (_.isEqual(currentVisibleRowIds, nextVisibleRowIds)) {
        return currentVisibleRowIds;
      }

      return nextVisibleRowIds;
    });
  }, []);

  const getComparableValue = useCallback((row, colKey) => {
    if (!row) {
      return undefined;
    }

    if (colKey === "ModbusRegister") {
      return parseJsonObject(row.DeviceParams, null)?.regnum;
    }
    if (colKey === "ModbusIP") {
      return parseJsonObject(row.DeviceParams, null)?.connection?.ip;
    }
    if (colKey === "ModbusPort") {
      const params = parseJsonObject(row.DeviceParams, null);
      if (row.SourceType === "ModbusTCP") {
        return params?.connection?.port;
      }
      return params?.connection?.port_number;
    }
    if (colKey === "ModbusSlave") {
      return parseJsonObject(row.DeviceParams, null)?.connection?.slave_id;
    }
    if (colKey === "Hidden") {
      return parseJsonObject(row.DynamicProperties, null)?.isHidden === true;
    }
    if (colKey === "SafetyShutdown") {
      return (
        parseJsonObject(row.DynamicProperties, null)?.flags?.[
          "Safety Shutdown"
        ] === true
      );
    }
    // The HWIO columns are transient, so compare the real fields they are parsed out of instead.
    if (colKey === "HwioIOType") {
      return row.TagTypeId;
    }
    if (colKey === "HwioChannelNumber") {
      return row.ChannelNumber;
    }
    if (colKey === "HwioBoardNumber") {
      return row.PortNumber;
    }

    return row[colKey];
  }, []);

  const getCellStyle = useCallback(
    ({ row, columnId }) => {
      // Columns that do not apply to this tag's source type read as inert rather than empty. Checked
      // first: such a cell can never be edited, so it can never also be dirty.
      if (row && !isColumnCompatibleWithTag(columnId, row, lookupValues)) {
        return { fontStyle: "italic", opacity: 0.6 };
      }

      if (!row || !dirtyTags.has(row.index) || !tags || !tags[row.index]) {
        return undefined;
      }

      const originalValue = getComparableValue(tags[row.index], columnId);
      const currentValue = getComparableValue(row, columnId);
      if (_.isEqual(originalValue, currentValue)) {
        return undefined;
      }

      return {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 214, 10, 0.22)"
            : "rgba(255, 214, 10, 0.35)",
      };
    },
    [dirtyTags, getComparableValue, lookupValues, tags, theme.palette.mode]
  );

  const handleSelectedColumnChange = (_, newValue) => {
    setSelectedColumns(newValue);
    localStorage.setItem(visibleColumnsStorageKey, JSON.stringify(newValue));
  };

  useEffect(() => {
    if (
      data &&
      data.some((row) => row.TagListName) &&
      !selectedColumns.includes("SourceTagList")
    ) {
      const updated = [...selectedColumns, "SourceTagList"];
      setSelectedColumns(updated);
      localStorage.setItem(visibleColumnsStorageKey, JSON.stringify(updated));
    }
  }, [data, selectedColumns]);

  const handleRowEdit = useCallback(
    ({ row, rowIndex, columnId }) => {
      const nextRow = { ...row };

      // Guard before touching anything. Entering edit mode on an incompatible cell is already blocked
      // by isCellEditable, but paste and fill apply across a whole range and reach this point — such
      // tags must be left exactly as they were rather than written with a value that means something
      // different for their own source type.
      if (!isColumnCompatibleWithTag(columnId, nextRow, lookupValues)) {
        setInfoMessages({
          open: true,
          message: `${getIncompatibleColumnMessage(columnId)} — tag skipped`,
          severity: "error",
        });
        return { cancel: true };
      }

      if (columnId === "SourceType") {
        // The dropdown offers DisplayNames ('HardWiredIO'), which do not always match the lookup's
        // InternalName ('HWIO'), so resolve against both rather than leaving TagSourceTypeId unset.
        nextRow.TagSourceTypeId = lookupNameOrDisplayNameToId(
          nextRow.SourceType,
          "TagSourceType",
          lookupValues
        );
        nextRow.DeviceParams =
          defaultDeviceParams[nextRow.SourceType] ?? nextRow.DeviceParams;
      }

      if (columnId === "SyncDirection") {
        const deviceParams = parseJsonObject(nextRow.DeviceParams, null);
        if (!deviceParams) {
          setInfoMessages({
            open: true,
            message: "Invalid DeviceParams",
            severity: "error",
          });
          return { cancel: true };
        }
        if (!deviceParams.properties_to_sync) {
          deviceParams.properties_to_sync = {
            properties: {},
            sync_direction: nextRow.SyncDirection || "None",
          };
        }
        deviceParams.properties_to_sync.sync_direction =
          nextRow.SyncDirection || "None";
        nextRow.DeviceParams = JSON.stringify(deviceParams);
      }

      if (columnId === "UniversalDescriptor") {
        const matchingTag = tagStandardTags.find(
          (tag) => tag.UniversalDescriptor === nextRow.UniversalDescriptor
        );
        if (matchingTag) {
          Object.assign(nextRow, matchingTag);
          delete nextRow.TCTagLayerId;
          delete nextRow.UpdatedBy;
          delete nextRow.UpdatedDate;
        }
      }

      if (
        columnId === "ModbusRegister" ||
        columnId === "ModbusIP" ||
        columnId === "ModbusPort" ||
        columnId === "ModbusSlave"
      ) {
        const deviceParams = parseJsonObject(nextRow.DeviceParams, null);
        if (!deviceParams) {
          setInfoMessages({
            open: true,
            message: "Invalid DeviceParams",
            severity: "error",
          });
          return { cancel: true };
        }

        if (columnId === "ModbusRegister") {
          deviceParams.regnum = nextRow.ModbusRegister;
        } else if (columnId === "ModbusIP") {
          deviceParams.connection = deviceParams.connection || {};
          deviceParams.connection.ip = nextRow.ModbusIP;
        } else if (columnId === "ModbusPort") {
          deviceParams.connection = deviceParams.connection || {};
          if (
            matchesSourceType(
              nextRow,
              MODBUS_TCP_SOURCE_TYPE_NAMES,
              lookupValues
            )
          ) {
            deviceParams.connection.port = nextRow.ModbusPort;
          } else {
            deviceParams.connection.port_number = nextRow.ModbusPort;
          }
        } else if (columnId === "ModbusSlave") {
          deviceParams.connection = deviceParams.connection || {};
          deviceParams.connection.slave_id = nextRow.ModbusSlave;
        }

        nextRow.DeviceParams = JSON.stringify(deviceParams);
      }

      if (hwioColumns.includes(columnId)) {
        if (columnId === "HwioIOType") {
          const tagTypeId = lookupNameOrDisplayNameToId(
            nextRow.HwioIOType,
            "TagType",
            lookupValues
          );
          if (tagTypeId === undefined) {
            setInfoMessages({
              open: true,
              message: `Unknown I/O Type "${nextRow.HwioIOType}"`,
              severity: "error",
            });
            return { cancel: true };
          }
          nextRow.TagTypeId = tagTypeId;
        } else if (columnId === "HwioChannelNumber") {
          // Shown 1-based and prefixed with the I/O type ("AI 5") to match the shared tag editor, but
          // stored 0-based. Parsing ignores the prefix so a pasted "AI 5", "DO 5" or "5" all agree.
          const displayChannelNumber = parseHwioChannelNumber(
            nextRow.HwioChannelNumber
          );
          if (
            !Number.isInteger(displayChannelNumber) ||
            displayChannelNumber < 1
          ) {
            setInfoMessages({
              open: true,
              message: "Channel Number must be a whole number of 1 or more",
              severity: "error",
            });
            return { cancel: true };
          }
          nextRow.ChannelNumber = displayChannelNumber - 1;
        } else if (columnId === "HwioBoardNumber") {
          const boardNumber = Number(nextRow.HwioBoardNumber);
          if (
            !hasValue(nextRow.HwioBoardNumber) ||
            !Number.isInteger(boardNumber)
          ) {
            setInfoMessages({
              open: true,
              message: "Board Number must be a whole number",
              severity: "error",
            });
            return { cancel: true };
          }
          nextRow.PortNumber = boardNumber;
        }
      }

      if (setpointColumns.includes(columnId)) {
        const deviceParams = parseJsonObject(nextRow.DeviceParams, null);
        if (!deviceParams) {
          setInfoMessages({
            open: true,
            message: "Invalid DeviceParams",
            severity: "error",
          });
          return { cancel: true };
        }

        nextRow.DeviceParams = JSON.stringify(
          addSetpointToDeviceParams(nextRow[columnId], deviceParams, columnId)
        );
      }

      if (columnId === "Hidden") {
        const dynamicProperties =
          parseJsonObject(nextRow.DynamicProperties, {}) || {};
        dynamicProperties.isHidden = nextRow.Hidden === true;
        nextRow.DynamicProperties = JSON.stringify(dynamicProperties);
      }

      if (columnId === "SafetyShutdown") {
        const dynamicProperties =
          parseJsonObject(nextRow.DynamicProperties, {}) || {};
        const flags = dynamicProperties.flags || {};
        if (nextRow.SafetyShutdown === true) {
          flags["Safety Shutdown"] = true;
        } else {
          delete flags["Safety Shutdown"];
        }

        if (Object.keys(flags).length > 0) {
          dynamicProperties.flags = flags;
        } else {
          delete dynamicProperties.flags;
        }

        nextRow.DynamicProperties = JSON.stringify(dynamicProperties);
      }

      const cleanedRow = removeTransientTagFields(nextRow);
      return { row: cleanedRow, value: cleanedRow[columnId], rowIndex };
    },
    [lookupValues, setInfoMessages, tagStandardTags]
  );

  const handleRowsChange = useCallback(
    (nextRows, metadata) => {
      const cleanedRows = nextRows.map((row) => removeTransientTagFields(row));
      setData(cleanedRows);

      const changedIndexes = metadata?.appliedEdits?.length
        ? [...new Set(metadata.appliedEdits.map((edit) => edit.rowIndex))]
        : cleanedRows.map((_, index) => index);

      setDirtyTags(
        updateDirtyTags({
          dirtyTags,
          editedTags: cleanedRows,
          tags,
          indices: changedIndexes,
        })
      );
    },
    [dirtyTags, setData, setDirtyTags, tags]
  );

  const leftColumns = useMemo(
    () => [
      createTextColumn("TagName", "Tag Name"),
      createTextColumn("TagIdentifier", "Tag Identifier"),
      {
        id: "UniversalDescriptor",
        accessorKey: "UniversalDescriptor",
        header: "Universal Descriptor",
        displayComponent: ({ row }) => (
          <TextDisplayCell value={row.UniversalDescriptor} />
        ),
        editComponent: ({ row, rowIndex, onCommit }) => (
          <UniversalDescriptorSelectCell
            row={row}
            rowIdx={rowIndex}
            onRowChange={(nextRow) => {
              onCommit(nextRow.UniversalDescriptor || "");
            }}
          />
        ),
      },
    ],
    []
  );

  const actionsColumn = useMemo(
    () => ({
      id: "Actions",
      accessorKey: "Actions",
      header: "Actions",
      sortable: false,
      searchable: false,
      editable: false,
      width: 160,
      minWidth: 160,
      showColumnControls: false,
      displayComponent: ({ row }) => (
        <>
          <IconButton onClick={() => deleteRow(row.index)}>
            <Delete />
          </IconButton>
          <IconButton onClick={() => duplicateTag(row.index)}>
            <CopyAll />
          </IconButton>
          <IconButton onClick={() => openTagDialog(row.index)}>
            <Edit />
          </IconButton>
        </>
      ),
    }),
    [deleteRow, duplicateTag, openTagDialog]
  );

  const selectedOptionalDefs = useMemo(() => {
    const optionalColumnDefs = createOptionalColumnDefs(
      codeLegends,
      data,
      lookupValues
    );
    return selectedColumns
      .map((columnName) => optionalColumnDefs[columnName])
      .filter(Boolean);
  }, [codeLegends, data, lookupValues, selectedColumns]);

  const columnStructure = useMemo(
    () => [...leftColumns, ...selectedOptionalDefs, actionsColumn],
    [actionsColumn, leftColumns, selectedOptionalDefs]
  );

  const normalizeColumnOrderAgainstCurrent = useCallback(
    (nextColumnOrder) => {
      if (!Array.isArray(nextColumnOrder) || nextColumnOrder.length === 0) {
        return null;
      }

      const availableColumnIds = columnStructure
        .map((column) => column.id || column.accessorKey)
        .filter(Boolean);
      const filtered = nextColumnOrder.filter((columnId) =>
        availableColumnIds.includes(columnId)
      );
      const additions = availableColumnIds.filter(
        (columnId) => !filtered.includes(columnId)
      );
      return [...filtered, ...additions];
    },
    [columnStructure]
  );

  const initialColumnOrder = useMemo(
    () => normalizeColumnOrderAgainstCurrent(columnOrder),
    [columnOrder, normalizeColumnOrderAgainstCurrent]
  );

  const handleColumnOrderChange = useCallback(
    (nextColumnOrder) => {
      const normalizedOrder =
        normalizeColumnOrderAgainstCurrent(nextColumnOrder);
      if (!normalizedOrder) {
        return;
      }

      setColumnOrder((existingOrder) => {
        if (_.isEqual(existingOrder, normalizedOrder)) {
          return existingOrder;
        }

        localStorage.setItem(
          visibleColumnOrderStorageKey,
          JSON.stringify(normalizedOrder)
        );
        return normalizedOrder;
      });
    },
    [normalizeColumnOrderAgainstCurrent]
  );

  useEffect(() => {
    const normalizedOrder = normalizeColumnOrderAgainstCurrent(columnOrder);
    if (!normalizedOrder) {
      return;
    }

    if (_.isEqual(normalizedOrder, columnOrder)) {
      return;
    }

    setColumnOrder(normalizedOrder);
    localStorage.setItem(
      visibleColumnOrderStorageKey,
      JSON.stringify(normalizedOrder)
    );
  }, [columnOrder, normalizeColumnOrderAgainstCurrent]);

  const exportColumns = useMemo(
    () => [
      { key: "select-row", name: "" },
      ...leftColumns.map((column) => ({ key: column.id, name: column.header })),
      ...selectedOptionalDefs.map((column) => ({
        key: column.id,
        name: column.header,
      })),
      { key: "Actions", name: "Actions" },
    ],
    [leftColumns, selectedOptionalDefs]
  );

  const handleExportAll = () => {
    exportToExcel(
      data,
      exportColumns,
      codeLegends,
      "tag-grid-export",
      data,
      lookupValues
    );
  };

  const handleExportSelected = () => {
    if (selectedTags && selectedTags.size > 0) {
      exportSelectedToExcel(
        data,
        exportColumns,
        selectedTags,
        codeLegends,
        "selected-tags-export",
        lookupValues
      );
    } else {
      setInfoMessages({
        open: true,
        message: "No rows selected for export",
        severity: "warning",
      });
    }
  };

  const selectedCount = selectedTags?.size ?? 0;

  const handleSelectAllRows = () => {
    if (!visibleRowIds || visibleRowIds.length === 0) {
      return;
    }

    setSelectedTags(new Set(visibleRowIds));
  };

  const handleClearSelection = () => {
    setSelectedTags(new Set());
  };

  const handleBulkRemoveConfirmClose = async (confirmed) => {
    setBulkRemoveConfirmOpen(false);

    if (!confirmed) {
      return;
    }

    if (typeof onBulkRemoveSelected === "function") {
      await onBulkRemoveSelected(new Set(selectedTags));
    }
  };

  return (
    <>
      <Stack direction={"row"} spacing={2} justifyContent={"right"}>
        <Autocomplete
          multiple
          limitTags={1}
          options={optionalColumns}
          getOptionLabel={(option) => optionalColumnLabels[option] ?? option}
          onChange={handleSelectedColumnChange}
          renderInput={(params) => (
            <TextField {...params} label='Columns' placeholder='Column' />
          )}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {optionalColumnLabels[option] ?? option}
              </li>
            );
          }}
          renderTags={() => {}}
          disableCloseOnSelect
          size='small'
          value={selectedColumns}
          sx={{ width: 250 }}
          disableListWrap
        />
        <SelectDistro distro={distro} setDistro={setDistro} />
        <Button
          aria-label='Clear Grid'
          startIcon={<PlusOneOutlined />}
          onClick={() => {
            addNewEmptyTag(0);
          }}
          variant='contained'>
          Add New Tag
        </Button>
        <Button
          aria-label='Clear Grid'
          startIcon={<DeleteOutline />}
          onClick={() => clearGrid()}
          variant='contained'>
          Clear Grid
        </Button>
        <Button
          aria-label='Reset Changes'
          startIcon={<UndoOutlined />}
          onClick={() => resetChanges()}
          variant='contained'>
          Reset Changes
        </Button>
      </Stack>
      <Divider sx={{ my: 2 }} />
      {selectedCount > 0 && (
        <>
          <Stack
            direction={"row"}
            spacing={2}
            justifyContent={"space-between"}
            alignItems={"center"}
            sx={{ mb: 2 }}>
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <Typography variant='body2' fontWeight={600}>
                {selectedCount} row{selectedCount !== 1 ? "s" : ""} selected
              </Typography>
              <Button
                variant='text'
                size='small'
                onClick={handleSelectAllRows}
                disabled={visibleRowIds.length === 0}>
                Select all rows
              </Button>
            </Stack>
            <Stack direction={"row"} spacing={1}>
              <Button
                variant='contained'
                color='error'
                startIcon={<Delete />}
                onClick={() => setBulkRemoveConfirmOpen(true)}>
                Remove Selected
              </Button>
              <Button
                variant='outlined'
                startIcon={<ClearIcon />}
                onClick={handleClearSelection}>
                Clear Selection
              </Button>
            </Stack>
          </Stack>
          <Divider sx={{ mb: 2 }} />
        </>
      )}
      <DataGrid
        rows={data}
        columns={columnStructure}
        onRowsChange={handleRowsChange}
        onRowEdit={handleRowEdit}
        initialColumnOrder={initialColumnOrder ?? undefined}
        onColumnOrderChange={handleColumnOrderChange}
        selectColumn
        selectedRows={selectedTags}
        onSelectedRowsChange={setSelectedTags}
        getRowId={(row) => row.index}
        getCellStyle={getCellStyle}
        alternateRowColors
        stickyRightColumnId='Actions'
        height={420}
        onVisibleRowIdsChange={handleVisibleRowIdsChange}
      />
      <ConfirmDialog
        open={bulkRemoveConfirmOpen}
        onClose={handleBulkRemoveConfirmClose}
        title='Remove Selected Tags'
        content={
          <Typography>
            Are you sure you want to remove {selectedCount} selected tag
            {selectedCount !== 1 ? "s" : ""} from the grid?
            <br></br>
            This only removes tags from the grid and does not delete or modify
            tags on any unit.
          </Typography>
        }
      />
      <br />
      <Stack direction={"row"} spacing={2} justifyContent={"right"}>
        <Button
          aria-label='Export All to Excel'
          startIcon={<FileDownload />}
          onClick={handleExportAll}
          variant='outlined'
          disabled={!data || data.length === 0}
          size='small'>
          Export All to Excel
        </Button>
        <Button
          aria-label='Export Selected to Excel'
          startIcon={<FileDownload />}
          onClick={handleExportSelected}
          variant='outlined'
          disabled={!selectedTags || selectedTags.size === 0}
          size='small'>
          Export Selected to Excel
        </Button>
      </Stack>
    </>
  );
};

export default TagDataGridWrapper;
